import { useState } from "react";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { ContactFormData, InquiryType } from "../types";

interface ContactFormProps {
  inquiryTypes: InquiryType[];
  onSubmit: (data: ContactFormData) => void;
}

const ContactForm = ({ inquiryTypes, onSubmit }: ContactFormProps) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    inquiryType: "",
    subject: "",
    message: "",
    preferredContact: "email",
  });

  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.inquiryType) {
      newErrors.inquiryType = "Please select an inquiry type";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 20) {
      newErrors.message = "Message must be at least 20 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch("https://formspree.io/f/xzzngloo", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          inquiryType: formData.inquiryType,
          subject: formData.subject,
          message: formData.message,
          preferredContact: formData.preferredContact,
          _origin: "contact",
        }),
      });

      if (!res.ok) {
        throw new Error("Submission failed");
      }

      onSubmit(formData);
      setFormData({
        name: "",
        email: "",
        inquiryType: "",
        subject: "",
        message: "",
        preferredContact: "email",
      });
      setErrors({});
    } catch (err) {
      console.error(err);
      setErrors((prev) => ({ ...prev, message: "Failed to send. Please try again." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    field: keyof ContactFormData,
    value: string | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Full Name"
          type="text"
          placeholder="Muhammad Fotih"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          error={errors.name}
          required
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="your.email@example.com"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          error={errors.email}
          required
        />
      </div>

      <Select
        label="Inquiry Type"
        description="Help us route your message to the right place"
        options={inquiryTypes}
        value={formData.inquiryType}
        onChange={(value) => handleChange("inquiryType", value)}
        error={errors.inquiryType}
        required
        placeholder="Select inquiry type"
      />

      <Input
        label="Subject"
        type="text"
        placeholder="Brief description of your inquiry"
        value={formData.subject}
        onChange={(e) => handleChange("subject", e.target.value)}
        error={errors.subject}
        required
      />

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Message <span className="text-destructive">*</span>
        </label>
        <textarea
          className="w-full px-4 py-3 rounded-lg border border-border bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 min-h-[150px] resize-y"
          placeholder="Tell me about your project, collaboration idea, or inquiry..."
          value={formData.message}
          onChange={(e) => handleChange("message", e.target.value)}
          required
        />
        {errors.message && (
          <p className="mt-1 text-sm text-destructive">{errors.message}</p>
        )}
      </div>

      <Select
        label="Preferred Contact Method"
        description="How would you like me to respond?"
        options={[
          { value: "email", label: "Email", description: "Professional email response" },
          { value: "whatsapp", label: "WhatsApp", description: "Quick mobile chat" },
          { value: "linkedin", label: "LinkedIn", description: "Professional network message" },
        ]}
        value={formData.preferredContact}
        onChange={(value) => handleChange("preferredContact", value)}
      />

      <Button
        type="submit"
        variant="default"
        size="lg"
        loading={isSubmitting}
        iconName="Send"
        iconPosition="right"
        fullWidth
        className="bg-primary hover:bg-primary/90"
      >
        {isSubmitting ? "Sending Message..." : "Send Message"}
      </Button>
    </form>
  );
};

export default ContactForm;